import { isLocalhost } from "@/helperMethods/util";
import { getTrackedNetworkStatus } from "@/helpers/networkStatusStorage";
import * as Sentry from "@sentry/vue";
import { GraphQLError } from "graphql";
import _ from "lodash";
import Vue from "vue";
import router from "./router";

/**
 * @description Init sentry package
 *
 * Quota protection for core KeyReply app vs future new applications:
 * - Errors from dashboard contribute to the shared org quota (same DSN/project currently).
 * - We drop known noise + support sampling via window.SENTRY_ERROR_SAMPLE_RATE (0-1).
 * - Recommendation: When adding new app, create a separate Sentry project (new DSN).
 *   Then use Sentry org Settings > Spend Allocations to reserve quota % for the new project.
 *   Also set Client Key rate limits on this project's DSN to cap core volume.
 * - Use "Delete & discard future events" in Issues for immediate repeating error suppression (no quota hit).
 */
export function initSentry() {
  if (isLocalhost()) {
    return;
  }

  Sentry.init({
    Vue,
    dsn: (window as any).SENTRY_DSN || "https://5186808e218b4da59e868263ccccff81@sentry.io/242275",
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop",
      "Login required",
      "Input Error:",
      "Non-Error promise rejection captured",
      "AbortError",
      "Script error.",
      "Loading chunk",
      "ChunkLoadError",
      "Network request failed",
    ],
    integrations: [
      Sentry.browserTracingIntegration({router})
    ],
    // attachProps defaults to true
    // trackComponents defauls to false
    tracesSampleRate: 0.2, // when test on dev, can disable to by pass CORS with `sentry-trace` header
    release: window.DASHBOARD_RELEASE_ID,
    environment: process.env.NODE_ENV,
    beforeBreadcrumb(breadcrumb, hint) {
      return breadcrumb.category === "ui.click" ? null : breadcrumb;
    },
    beforeSend(event: Sentry.Event) {
      const exception = event.exception;
      // Check if it is an exception, if so, show the report dialog
      if (exception) {
        const message: any = _.get(exception, "values[0].value");

        // Only check for exception message as string,
        // if message is object (due to wrong error capturing), simply ignore the event
        if (typeof message !== "string") {
          return null;
        }

        const isGraphQLPermissionError = message.match(
          /GraphQL error: 401: User does not have permission/i
        );
        const isHttpPermissionError = message.match(/403/i);
        const is4XXError = isGraphQLPermissionError || isHttpPermissionError || /"status"\s*:\s*4\d{2}/i.test(message) || /\b4\d{2}\b/.test(message);
        const isNetworkError = message.match(
          /network error|timeout of 0ms exceeded|xhr post error|xhr poll error|failed to fetch|net::err/i
        );
        const isInputOrKnown = /Input Error:/i.test(message);

        if (is4XXError || isNetworkError || isInputOrKnown) {
          return null;
        }

        // Drop other common GraphQL client/validation noise
        if (/GraphQL error: (4|Bad Request|Unauthorized|Forbidden|validation)/i.test(message)) {
          return null;
        }
      }

      // Probabilistic sampling to protect quota headroom for new apps (set window.SENTRY_ERROR_SAMPLE_RATE = 0.25 for 25%).
      // Applied only to events that survived the hard ignore filters above.
      const sampleRate = (window as any).SENTRY_ERROR_SAMPLE_RATE != null
        ? parseFloat((window as any).SENTRY_ERROR_SAMPLE_RATE)
        : 1.0;
      if (sampleRate < 1.0 && Math.random() > sampleRate) {
        return null;
      }

      // attach network statuses
      const trackedNetworkStatuses = getTrackedNetworkStatus();
      if (trackedNetworkStatuses && trackedNetworkStatuses.length > 0) {
        event.breadcrumbs?.push({
          level: "info",
          data: {
            trackedNetworkStatuses: JSON.stringify(trackedNetworkStatuses, null, 2),
          },
        });
      }
      return event;
    },
  } as any);

  Sentry.setTag("application", "keyreply-core");
  Sentry.setTag("component", "dashboard");
  Sentry.setTag("module", "dashboard");

  // @ts-ignore
  window.testSentry = () => {
    Sentry.captureException(new Error(`Test Sentry ${new Date().toISOString()}`));
  };
}

// Simple rate limiter for GraphQL error captures (Cluster 5 - dashboard does many background queries)
const graphQLErrorRateLimit: Record<string, number> = {};
export function captureGraphQLError(operation: any, err: GraphQLError) {
  const { message } = err;
  const key = (message || "").slice(0, 100);
  const now = Date.now();
  if (graphQLErrorRateLimit[key] && now - graphQLErrorRateLimit[key] < 15000) {
    return; // suppress duplicates within 15s
  }
  graphQLErrorRateLimit[key] = now;

  Sentry.withScope((scope) => {
    const { message, locations, path, extensions } = err;
    const graphQLPath = Array.isArray(path) ? path.join(" > ") : "";

    if (graphQLPath) {
      scope.addBreadcrumb({
        category: "query-path",
        message: graphQLPath,
        level: "debug",
      });
    }
    scope.setExtra("path", graphQLPath);
    scope.setExtra("variables", _.get(operation, "variables"));
    scope.setExtra("message", message);
    scope.setExtra("locations", locations);
    scope.setExtra("extensions", extensions);
    Sentry.captureException(new GraphQLError(`GraphQL Error: ${graphQLPath} "${message}"`));
  });
}

export const logAxiosExceptionToSentry = (error: any) => {
  const status = error?.response?.status || error?.request?.status;

  // Low hanging quota protection: only report server errors (5xx) and true network failures to Sentry.
  // 4xx client errors (bad requests, validation, rate limits, permission handled by beforeSend/UI)
  // were a major source of high-frequency client-side noise from the admin dashboard.
  if (status && status >= 400 && status < 500) {
    return; // skip Sentry for client errors
  }

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    Sentry.captureException(
      new Error(
        JSON.stringify({
          response: error.response.data,
          status: error.response.status,
          url: (error.response.config && error.response.config.url) || "",
        })
      )
    );
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser
    // - see: https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest
    // and an instance of http.ClientRequest in node.js
    Sentry.captureException(
      new Error(
        JSON.stringify({
          response: error.request.responseText || "",
          status: error.request.status,
          url: error.request.responseURL || "",
        })
      )
    );
  } else {
    // Something happened in setting up the request that triggered an Error
    Sentry.captureException(new Error(error.message));
  }
};

export function enrichSentryUserData(user: any) {
  if (!user) {
    Sentry.configureScope((scope) => scope.setUser(null));
  } else {
    Sentry.setUser({ email: user.email });
  }
}
